import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  Grid,
  Divider,
  Avatar,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Email as EmailIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Verified as VerifiedIcon,
  Payment as PaymentIcon,
  Interests as InterestsIcon,
  Business as BusinessIcon,
  Schedule as ScheduleIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  History as HistoryIcon,
  ConfirmationNumber as TicketIcon,
  Edit as EditIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import { FabrikUser, CheckInResponse, DayPassResponse, MembershipTierResponse, Gathering } from '../../../types';
import { useAuth } from '../../../contexts/AuthContext';
import { useSpaces } from '../../gatherings/hooks/data/useSpaces';
import { fetchUserRecentCheckIns, fetchUserDayPasses, fetchMembershipTiers, sendPasswordResetEmail, removeCaasMembership } from '../../../services/api/endpoints/users';
import { fetchUserAttendedGatherings } from '../../../services/api/endpoints/gatherings';
import { useAdminCheckin } from '../../space-ops/hooks/useAdminCheckin';
import UserNotes from './UserNotes';
import UserProfileInsights from './UserProfileInsights';
import EditUserProfileDialog from './EditUserProfileDialog';

interface UserDetailContentProps {
  user: FabrikUser;
  onCheckInClick?: () => void;
  onUserDataRefresh?: () => void;
}

const UserDetailContent: React.FC<UserDetailContentProps> = ({ user, onCheckInClick, onUserDataRefresh }) => {
  const { user: authUser } = useAuth();
  const [checkIns, setCheckIns] = useState<CheckInResponse[]>([]);
  const [dayPasses, setDayPasses] = useState<DayPassResponse[]>([]);
  const [membershipTiers, setMembershipTiers] = useState<MembershipTierResponse[]>([]);
  const [checkInsLoading, setCheckInsLoading] = useState(false);
  const [dayPassesLoading, setDayPassesLoading] = useState(false);
  const [membershipTiersLoading, setMembershipTiersLoading] = useState(false);
  const [checkInsError, setCheckInsError] = useState<string | null>(null);
  const [dayPassesError, setDayPassesError] = useState<string | null>(null);
  const [membershipTiersError, setMembershipTiersError] = useState<string | null>(null);
  const [showCheckInSection, setShowCheckInSection] = useState(false);
  const [selectedSpaceId, setSelectedSpaceId] = useState<string>('');
  const [showDayPassSection, setShowDayPassSection] = useState(false);
  const [selectedDayPassSpaceId, setSelectedDayPassSpaceId] = useState<string>('');
  const [sendingPasswordReset, setSendingPasswordReset] = useState(false);
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);
  const [passwordResetError, setPasswordResetError] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [attendedGatherings, setAttendedGatherings] = useState<Gathering[]>([]);
  const [gatheringsLoading, setGatheringsLoading] = useState(false);
  const [gatheringsError, setGatheringsError] = useState<string | null>(null);
  const [showRemoveCaasDialog, setShowRemoveCaasDialog] = useState(false);
  const [isRemovingCaas, setIsRemovingCaas] = useState(false);
  const [removeCaasError, setRemoveCaasError] = useState<string | null>(null);

  // Load spaces data for space name mapping
  const { spaces, isLoading: spacesLoading, error: spacesError } = useSpaces(authUser?.token || null);

  // Initialize admin check-in hook
  const { checkInUser, isCheckingIn, checkInError, checkInSuccess } = useAdminCheckin({
    onSuccess: () => {
      // Reset form and close section after successful check-in
      setSelectedSpaceId('');
      setShowCheckInSection(false);
      // Refresh user data to show updated quota
      onUserDataRefresh?.();
    },
    onError: (error) => {
      console.error('Check-in failed:', error);
    }
  });

  // Initialize admin check-in hook for day pass usage
  const { 
    checkInUser: redeemDayPass, 
    isCheckingIn: isUsingDayPass, 
    checkInError: dayPassError, 
    checkInSuccess: dayPassSuccess 
  } = useAdminCheckin({
    onSuccess: () => {
      // Reset form and close section after successful day pass usage
      setSelectedDayPassSpaceId('');
      setShowDayPassSection(false);
      // Refresh user data to show updated quota
      onUserDataRefresh?.();
      loadCheckIns();
    },
    onError: (error) => {
      console.error('Day pass usage failed:', error);
    }
  });

  // Helper function to get space name from space ID
  const getSpaceName = (spaceId: string): string => {
    if (spacesLoading) return 'Loading...';
    if (spacesError) return 'Unknown Space';
    
    const space = spaces.find(s => s.id === spaceId);
    return space?.name || 'Unknown Space';
  };

  // Helper function to check if a date is today
  const isToday = (dateString: string): boolean => {
    const checkInDate = new Date(dateString);
    const today = new Date();
    return checkInDate.toDateString() === today.toDateString();
  };

  // Helper function to check if a billing date is in the past
  const isBillingDatePast = (dateString: string | null | undefined): boolean => {
    // If no billing date provided, consider it invalid (past due)
    if (!dateString) {
      return true;
    }
    
    try {
      // Parse YYYY-MM-DD as a local date (no timezone) to avoid shifting a day earlier
      let billingDate: Date;
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [y, m, d] = dateString.split('-').map(Number);
        billingDate = new Date(y, m - 1, d);
      } else {
        billingDate = new Date(dateString);
      }
      
      // Check if the date is valid
      if (isNaN(billingDate.getTime())) {
        return true; // Invalid date, consider it past due
      }
      
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
      billingDate.setHours(0, 0, 0, 0);
      return billingDate < today;
    } catch (error) {
      // If any error occurs parsing the date, consider it past due
      return true;
    }
  };

  // Helper to determine if user has valid billing for enabling check-in
  const getHasValidBilling = (): boolean => {
    const subs = user.subscription_details || [];
    const isActiveOrTrialing = (status?: string) => {
      const s = status?.toLowerCase();
      return s === 'active' || s === 'trialing';
    };
    const isScheduled = (status?: string) => status?.toLowerCase() === 'scheduled';

    const hasActiveOrTrialing = subs.some(sub => isActiveOrTrialing(sub.subscription_status));
    const hasActiveOrTrialingWithFuture = subs.some(
      sub => isActiveOrTrialing(sub.subscription_status) && !!sub.next_billing_date && !isBillingDatePast(sub.next_billing_date)
    );
    const hasScheduledWithFuture = subs.some(
      sub => isScheduled(sub.subscription_status) && !!sub.next_billing_date && !isBillingDatePast(sub.next_billing_date)
    );

    // Valid if an active/trialing sub has a future billing date
    // OR if there is an active/trialing sub (even if past) AND a scheduled sub with a future billing date
    return hasActiveOrTrialingWithFuture || (hasActiveOrTrialing && hasScheduledWithFuture);
  };

  // Helper function to load check-ins data
  const loadCheckIns = useCallback(() => {
    if (authUser?.token && user.id) {
      // Check if user has valid billing based on active/trialing and scheduled rules
      const hasValidBilling = getHasValidBilling();
      
      // Don't make API call if billing is invalid - let UI show friendly message
      if (!hasValidBilling) {
        setCheckInsLoading(false);
        setCheckInsError(null);
        setCheckIns([]); // Set empty array so UI shows the friendly message
        return;
      }
      
      setCheckInsLoading(true);
      setCheckInsError(null);
      fetchUserRecentCheckIns(authUser.token, user.id, true)
        .then(setCheckIns)
        .catch((error) => {
          console.error('Error loading check-ins:', error);
          setCheckInsError('Failed to load check-in history');
        })
        .finally(() => setCheckInsLoading(false));
    }
  }, [authUser?.token, user.id]);

  // Helper function to calculate usage data
  const calculateUsageData = () => {
    // Get user's active membership tier name from subscription
    const activeSubscription = user.subscription_details?.find(sub => 
      sub.subscription_status === 'active' || sub.subscription_status === 'trialing'
    );
    
    const userTierName = activeSubscription?.product_name;
    if (!userTierName || membershipTiers.length === 0) {
      return { usedDays: 0, totalDays: 0, isUnlimited: false };
    }

    // Find the matching tier
    const userTier = membershipTiers.find(tier => tier.name === userTierName);
    if (!userTier) {
      return { usedDays: 0, totalDays: 0, isUnlimited: false };
    }

    // If days_in_space is null or undefined, it means unlimited
    if (userTier.days_in_space == null) {
      return { usedDays: 0, totalDays: 0, isUnlimited: true };
    }

    // Count check-ins that used membership days (not day passes)
    const membershipCheckIns = checkIns.filter(checkIn => 
      checkIn.used_quota_type === 'membership day'
    );

    return {
      usedDays: membershipCheckIns.length,
      totalDays: userTier.days_in_space,
      isUnlimited: false
    };
  };

  // Load check-ins and day passes data
  useEffect(() => {
    if (authUser?.token && user.id) {
      // Load recent check-ins
      loadCheckIns();
      // Load all day passes (not just active ones)
      setDayPassesLoading(true);
      setDayPassesError(null);
      fetchUserDayPasses(authUser.token, user.id, false, true)
        .then((passes) => {
          // Sort: unused (active) at top, used below in order of usage date
          const sorted = passes.sort((a, b) => {
            // Active passes first
            if (!a.used_on && b.used_on) return -1;
            if (a.used_on && !b.used_on) return 1;
            
            // If both active or both used, sort by date
            if (!a.used_on && !b.used_on) {
              return new Date(b.purchase_date).getTime() - new Date(a.purchase_date).getTime();
            }
            
            if (a.used_on && b.used_on) {
              return new Date(b.used_on).getTime() - new Date(a.used_on).getTime();
            }
            
            return 0;
          });
          setDayPasses(sorted);
        })
        .catch((error) => {
          console.error('Error loading day passes:', error);
          setDayPassesError('Failed to load day passes');
        })
        .finally(() => setDayPassesLoading(false));

      // Load membership tiers
      setMembershipTiersLoading(true);
      setMembershipTiersError(null);
      fetchMembershipTiers(authUser.token)
        .then(setMembershipTiers)
        .catch((error) => {
          console.error('Error loading membership tiers:', error);
          setMembershipTiersError('Failed to load membership tiers');
        })
        .finally(() => setMembershipTiersLoading(false));

      // Load attended gatherings
      setGatheringsLoading(true);
      setGatheringsError(null);
      fetchUserAttendedGatherings(authUser.token, user.id)
        .then(setAttendedGatherings)
        .catch((error) => {
          console.error('Error loading attended gatherings:', error);
          setGatheringsError('Failed to load attended gatherings');
        })
        .finally(() => setGatheringsLoading(false));
    }
  }, [authUser?.token, user.id]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';

    // If it's a date-only string, construct as local date to avoid timezone shifts
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [y, m, d] = dateString.split('-').map(Number);
      const localDate = new Date(y, m - 1, d);
      return localDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }

    const d = new Date(dateString);
    if (isNaN(d.getTime())) return 'N/A';
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return 'N/A';
    return d.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatTime = (timeString: string): string => {
    // Check if the time already includes AM/PM
    if (timeString.includes('AM') || timeString.includes('PM')) {
      return timeString;
    }
    
    // If not, assume timeString is in format "HH:MM:SS" or "HH:MM"
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const formatGatheringDate = (dateString: string): string => {
    // Parse the date string manually (format: YYYY-MM-DD)
    const [year, month, day] = dateString.split('-');
    
    // Convert month number to month name
    const monthNames = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    const monthName = monthNames[parseInt(month) - 1];
    
    // Remove leading zero from day if present
    const dayNum = parseInt(day).toString();
    
    // Format as "Mar 22, 2025"
    return `${monthName} ${dayNum}, ${year}`;
  };

  const formatAddress = (address?: any) => {
    if (!address) return 'No address provided';
    const parts = [
      address.address1,
      address.address2,
      address.city,
      address.state,
      address.zip_code,
      address.country,
    ].filter(Boolean);
    return parts.join(', ');
  };

  const getQuotaTypeDisplay = (quotaType: string) => {
    switch (quotaType) {
      case 'membership day':
        return { label: 'Membership Day', color: 'primary' as const };
      case 'day pass':
        return { label: 'Day Pass', color: 'secondary' as const };
      default:
        return { label: quotaType, color: 'default' as const };
    }
  };

  const getDayPassStatusDisplay = (status: string, usedOn?: string) => {
    if (usedOn) {
      return { label: 'Used', color: 'default' as const };
    }
    
    switch (status.toLowerCase()) {
      case 'active':
        return { label: 'Active', color: 'success' as const };
      case 'expired':
        return { label: 'Expired', color: 'error' as const };
      default:
        return { label: status, color: 'default' as const };
    }
  };

  const handleSendPasswordReset = async () => {
    if (!authUser?.token) return;
    
    setSendingPasswordReset(true);
    setPasswordResetError(null);
    setPasswordResetSuccess(false);
    
    try {
      await sendPasswordResetEmail(authUser.token, user.id);
      setPasswordResetSuccess(true);
      
      // Auto-dismiss success message after 5 seconds
      setTimeout(() => {
        setPasswordResetSuccess(false);
      }, 5000);
    } catch (error) {
      console.error('Failed to send password reset:', error);
      setPasswordResetError('Failed to send password reset email. Please try again.');
    } finally {
      setSendingPasswordReset(false);
    }
  };

  const handleRemoveCaasMembership = async () => {
    if (!authUser?.token) return;
    
    setIsRemovingCaas(true);
    setRemoveCaasError(null);
    
    try {
      await removeCaasMembership(authUser.token, user.id);
      setShowRemoveCaasDialog(false);
      // Refresh user data to reflect the changes
      onUserDataRefresh?.();
    } catch (error) {
      console.error('Failed to remove CaaS membership:', error);
      setRemoveCaasError('Failed to remove CaaS membership. Please try again.');
    } finally {
      setIsRemovingCaas(false);
    }
  };

  return (
    <Box>
      {/* Header Section with User Info and ID */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
              {user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown User'}
            </Typography>
            <IconButton 
              size="small" 
              onClick={() => setEditDialogOpen(true)}
              sx={{ color: 'text.secondary' }}
              aria-label="Edit user profile"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
            <Chip
              icon={user.email_verified ? <VerifiedIcon /> : undefined}
              label={user.email_verified ? 'Verified' : 'Unverified'}
              color={user.email_verified ? 'success' : 'warning'}
              size="small"
            />
            <Typography variant="caption" color="text.secondary">
              ID: {user.id}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Profile and Settings */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Profile */}
            <Paper sx={{ p: 2, height: 'fit-content' }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon color="primary" />
                Profile
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                <Avatar
                  src={user.image_url}
                  sx={{ width: 80, height: 80, mb: 1 }}
                >
                  {(user.first_name?.[0] || '') + (user.last_name?.[0] || '')}
                </Avatar>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  {user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim()}
                </Typography>
              </Box>

              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <EmailIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Email"
                    secondary={user.email}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <PersonIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Name"
                    secondary={`${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Not provided'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <SecurityIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Firebase ID"
                    secondary={user.firebase_id || 'Not available'}
                  />
                </ListItem>
              </List>
            </Paper>

            {/* Homebase and Settings */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationIcon color="primary" />
                Homebase and Settings
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                {/* Location Information */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" color="text.secondary">Home Space</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {user.space?.name || 'No space assigned'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" color="text.secondary">Address</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {formatAddress(user.address)}
                  </Typography>
                </Grid>
                
                {/* Preferences */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" color="text.secondary">Interests</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {user.interests && user.interests.length > 0 ? (
                      user.interests.map((interest, index) => (
                        <Chip key={index} label={interest} size="small" variant="outlined" />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No interests specified
                      </Typography>
                    )}
                  </Box>
                </Grid>
                {user.expectations && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="body2" color="text.secondary">Expectations</Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {user.expectations}
                    </Typography>
                  </Grid>
                )}
                
                {/* System Settings */}
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    System Settings
                  </Typography>
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">Push Notifications</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {user.push_notification_token ? 'Enabled' : 'Disabled'}
                  </Typography>
                </Grid>
                {user.hubspot && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">HubSpot Integration</Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      Connected (ID: {user.hubspot.id})
                    </Typography>
                  </Grid>
                )}
                
                {/* HubSpot Details */}
                {user.hubspot && user.hubspot.properties && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      HubSpot Details
                    </Typography>
                    <Box sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                      {user.hubspot.properties.fabrik_lifecycle_stage && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Lifecycle Stage: {user.hubspot.properties.fabrik_lifecycle_stage}
                        </Typography>
                      )}
                      {user.hubspot.properties.fabrik_location && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Location: {user.hubspot.properties.fabrik_location}
                        </Typography>
                      )}
                      {user.hubspot.properties.access_tier && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Access Tier: {user.hubspot.properties.access_tier}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Box>
        </Grid>

        {/* Right Column - Membership, Check-ins and Day Passes */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Membership Section */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <BusinessIcon color="primary" />
                Account & Membership
              </Typography>
              
              {/* Account Status */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom sx={{ color: 'text.primary', mb: 1.5 }}>
                  Account Status
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        Onboarding
                      </Typography>
                      <Chip
                        label={user.onboarding_done ? 'Complete' : 'Incomplete'}
                        color={user.onboarding_done ? 'success' : 'warning'}
                        size="small"
                        sx={{ fontWeight: 'medium' }}
                      />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        Password
                      </Typography>
                      <Chip
                        label={user.has_password ? 'Set' : 'Not Set'}
                        color={user.has_password ? 'success' : 'default'}
                        size="small"
                        sx={{ fontWeight: 'medium' }}
                      />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        Password Reset
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={sendingPasswordReset}
                        onClick={handleSendPasswordReset}
                        sx={{ fontWeight: 'medium' }}
                      >
                        {sendingPasswordReset ? <CircularProgress size={20} /> : 'Send Reset Email'}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
                {passwordResetSuccess && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    Password reset email sent successfully to {user.email}
                  </Alert>
                )}
                {passwordResetError && (
                  <Alert severity="error" sx={{ mt: 2 }} onClose={() => setPasswordResetError(null)}>
                    {passwordResetError}
                  </Alert>
                )}
              </Box>

              {/* Active Subscriptions */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom sx={{ color: 'text.primary', mb: 1.5 }}>
                  Active Subscriptions
                </Typography>
                {user.subscription_details && user.subscription_details.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {user.subscription_details.map((subscription, index) => (
                      <Box 
                        key={index} 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 2,
                          p: 2, 
                          bgcolor: 'success.50', 
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'success.200'
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" fontWeight="medium" color="success.dark">
                            {subscription.product_name}
                          </Typography>
                          {subscription.next_billing_date ? (
                            <Typography 
                              variant="caption" 
                              color={isBillingDatePast(subscription.next_billing_date) ? 'error' : 'text.secondary'}
                              sx={isBillingDatePast(subscription.next_billing_date) ? { fontWeight: 'medium' } : {}}
                            >
                              Next billing: {formatDate(subscription.next_billing_date)}
                            </Typography>
                          ) : (
                            <Typography 
                              variant="caption" 
                              color="error"
                              sx={{ fontWeight: 'medium' }}
                            >
                              Invalid next billing date
                            </Typography>
                          )}
                        </Box>
                        <Chip
                          label={subscription.subscription_status || 'Active'}
                          color={subscription.subscription_status === 'Active' ? 'success' : 'default'}
                          size="small"
                          variant="filled"
                          sx={{ fontWeight: 'medium' }}
                        />
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ 
                    textAlign: 'center', 
                    p: 3, 
                    bgcolor: 'grey.50', 
                    borderRadius: 2,
                    border: '1px dashed',
                    borderColor: 'grey.300'
                  }}>
                    <Typography variant="body2" color="text.secondary">
                      No active subscriptions
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* CaaS Membership */}
              {user.caas_data?.is_caas_user && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="medium" sx={{ color: 'text.primary', mb: 1.5 }}>
                    CaaS Membership
                  </Typography>
                  {user.caas_data.teams && user.caas_data.teams.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {user.caas_data.teams.map((team) => (
                        <Box 
                          key={team.team_id}
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 2,
                            p: 2, 
                            bgcolor: 'rgba(111, 66, 193, 0.08)', 
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'rgba(111, 66, 193, 0.3)'
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Chip
                              label="CaaS Member"
                              size="small"
                              sx={{
                                bgcolor: '#6f42c1',
                                color: 'white',
                                fontWeight: 'medium',
                                mb: 1
                              }}
                            />
                            <Typography variant="body1" fontWeight="medium" sx={{ color: '#6f42c1' }}>
                              {team.client_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Team: {team.team_name}
                            </Typography>
                          </Box>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => setShowRemoveCaasDialog(true)}
                          >
                            Remove
                          </Button>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 2,
                        p: 2, 
                        bgcolor: 'rgba(111, 66, 193, 0.08)', 
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'rgba(111, 66, 193, 0.3)'
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Chip
                          label="CaaS Member"
                          size="small"
                          sx={{
                            bgcolor: '#6f42c1',
                            color: 'white',
                            fontWeight: 'medium',
                            mb: 1
                          }}
                        />
                        <Typography variant="body1" fontWeight="medium" sx={{ color: '#6f42c1' }}>
                          No Client Assigned
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Team: No Team Assigned
                        </Typography>
                      </Box>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => setShowRemoveCaasDialog(true)}
                      >
                        Remove
                      </Button>
                    </Box>
                  )}
                </Box>
              )}

              {/* Usage Summary - Days Available and Day Passes */}
              {!membershipTiersLoading && (
                <Box sx={{ mb: 0 }}>
                  <Typography variant="subtitle1" fontWeight="medium" gutterBottom sx={{ color: 'text.primary', mb: 1.5 }}>
                    Current Usage
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    p: 2, 
                    bgcolor: 'primary.50', 
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'primary.200',
                    gap: 2
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6" fontWeight="bold" color="primary.main">
                          {(() => {
                            const usageData = calculateUsageData();
                            if (usageData.isUnlimited) {
                              return 'Unlimited';
                            } else if (usageData.totalDays === 0) {
                              return 'No Access';
                            } else {
                              return `${usageData.usedDays}/${usageData.totalDays}`;
                            }
                          })()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Days Used
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={(() => {
                          // Use the same calculations that work for the display
                          const usageData = calculateUsageData();
                          
                          // Check if user has available membership days
                          const hasAvailableDays = usageData.isUnlimited || (usageData.totalDays > 0 && usageData.usedDays < usageData.totalDays);
                          
                          // Updated billing validity: active/trialing with future billing OR active/trialing (even if past) + scheduled with future billing
                          const hasValidBilling = getHasValidBilling();
                          
                          // Only enable if user has available membership days AND valid billing
                          return !hasAvailableDays || !hasValidBilling;
                        })()}
                        sx={{ 
                          fontWeight: 'medium',
                          '&:hover': { bgcolor: 'primary.600' }
                        }}
                        onClick={() => setShowCheckInSection(!showCheckInSection)}
                      >
                        {showCheckInSection ? 'Cancel Check-in' : 'Check In'}
                      </Button>
                    </Box>
                    
                    <Box sx={{ height: '30px', width: '1px', bgcolor: 'primary.300' }} />
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, justifyContent: 'flex-end' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6" fontWeight="bold" color="secondary.main">
                          {dayPasses.filter(pass => pass.status === 'active' && !pass.used_on).length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Day Passes
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        disabled={(() => {
                          // Use the same calculations that work for the display
                          const usageData = calculateUsageData();
                          const activeDayPasses = dayPasses.filter(pass => pass.status === 'active' && !pass.used_on);
                          
                          // Check if user has active day passes
                          const hasActiveDayPasses = activeDayPasses.length > 0;
                          
                          // Check if user has available membership days
                          const hasAvailableDays = usageData.isUnlimited || (usageData.totalDays > 0 && usageData.usedDays < usageData.totalDays);
                          
                          // Only enable if user has NO available membership days AND has active day passes
                          return hasAvailableDays || !hasActiveDayPasses;
                        })()}
                        sx={{ 
                          fontWeight: 'medium',
                          '&:hover': { bgcolor: 'secondary.600' }
                        }}
                        onClick={() => setShowDayPassSection(!showDayPassSection)}
                      >
                        {showDayPassSection ? 'Hide Day Passes' : 'Use Pass'}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              )}
            </Paper>

            {/* Check-in Section */}
            {showCheckInSection && (
              <Paper sx={{ p: 2, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
                  Check In User
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select a space to check in this user. This will use their available quota (membership days or day passes).
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel id="space-select-label">Select Space</InputLabel>
                    <Select
                      labelId="space-select-label"
                      id="space-select"
                      value={selectedSpaceId}
                      label="Select Space"
                      size="small"
                      onChange={(event) => setSelectedSpaceId(event.target.value as string)}
                    >
                      {spaces.map((space) => (
                        <MenuItem key={space.id} value={space.id}>
                          {space.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!selectedSpaceId}
                    sx={{ fontWeight: 'medium' }}
                    onClick={() => {
                      if (selectedSpaceId) {
                        checkInUser(user.id, selectedSpaceId);
                      }
                    }}
                  >
                    Check In
                  </Button>
                </Box>
              </Paper>
            )}

            {/* Day Pass Section */}
            {showDayPassSection && (
              <Paper sx={{ p: 2, bgcolor: 'secondary.50', border: '1px solid', borderColor: 'secondary.200' }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'secondary.main' }}>
                  Use Day Pass
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select a space to use a day pass for this user.
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel id="day-pass-space-select-label">Select Space</InputLabel>
                    <Select
                      labelId="day-pass-space-select-label"
                      id="day-pass-space-select"
                      value={selectedDayPassSpaceId}
                      label="Select Space"
                      size="small"
                      onChange={(event) => setSelectedDayPassSpaceId(event.target.value as string)}
                    >
                      {spaces.map((space) => (
                        <MenuItem key={space.id} value={space.id}>
                          {space.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <Button
                    variant="contained"
                    color="secondary"
                    disabled={!selectedDayPassSpaceId}
                    sx={{ fontWeight: 'medium' }}
                    onClick={() => {
                      if (selectedDayPassSpaceId) {
                        redeemDayPass(user.id, selectedDayPassSpaceId, true);
                      }
                    }}
                  >
                    Use Day Pass
                  </Button>
                </Box>
              </Paper>
            )}

            {/* Recent Check-ins Section */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HistoryIcon color="primary" />
                Check-ins
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {checkInsLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
              
              {checkInsError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {checkInsError}
                </Alert>
              )}
              
              {!checkInsLoading && !checkInsError && (
                <>
                  {checkIns.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date & Time</TableCell>
                            <TableCell>Space</TableCell>
                            <TableCell>Check In Type</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {checkIns.map((checkIn) => {
                            const quotaDisplay = getQuotaTypeDisplay(checkIn.used_quota_type);
                            return (
                              <TableRow key={checkIn.id}>
                                <TableCell>
                                  <Typography 
                                    variant="body2"
                                    sx={{ 
                                      color: isToday(checkIn.timestamp) ? 'success.main' : 'text.primary',
                                      fontWeight: isToday(checkIn.timestamp) ? 'medium' : 'normal'
                                    }}
                                  >
                                    {formatDateTime(checkIn.timestamp)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {getSpaceName(checkIn.space_id)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={quotaDisplay.label}
                                    color={quotaDisplay.color}
                                    size="small"
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      {user.subscription_details && user.subscription_details.some(sub => 
                        (sub.subscription_status === 'active' || sub.subscription_status === 'trialing') && 
                        (!sub.next_billing_date || isBillingDatePast(sub.next_billing_date))
                      ) ? 
                        "Unable to load check-ins due to billing date issues. Please contact support." : 
                        "No recent check-ins found"
                      }
                    </Typography>
                  )}
                </>
              )}
            </Paper>

            {/* Day Passes Section */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TicketIcon color="primary" />
                Day Passes
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {dayPassesLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
              
              {dayPassesError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {dayPassesError}
                </Alert>
              )}
              
              {!dayPassesLoading && !dayPassesError && (
                <>
                  {dayPasses.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Status</TableCell>
                            <TableCell>Purchase Date</TableCell>
                            <TableCell>Expiration Date</TableCell>
                            <TableCell>Used Date</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {dayPasses.map((dayPass) => {
                            const statusDisplay = getDayPassStatusDisplay(dayPass.status, dayPass.used_on);
                            return (
                              <TableRow key={dayPass.id}>
                                <TableCell>
                                  <Chip
                                    label={statusDisplay.label}
                                    color={statusDisplay.color}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {formatDate(dayPass.purchase_date)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {formatDate(dayPass.expires_on)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {dayPass.used_on ? formatDate(dayPass.used_on) : '-'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      No day passes found
                    </Typography>
                  )}
                  
                  {/* Display active day passes summary */}
                  {user.usage_data && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Day Pass Summary
                      </Typography>
                      <Typography variant="body2">
                        <strong>Active Day Passes:</strong>{' '}
                        <span style={{ 
                          color: user.usage_data.active_day_passes_count > 0 ? '#2e7d32' : '#d32f2f'
                        }}>
                          {user.usage_data.active_day_passes_count}
                        </span>
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Paper>

            {/* Attended Gatherings Section */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EventIcon color="primary" />
                Attended Gatherings
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {gatheringsLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
              
              {gatheringsError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {gatheringsError}
                </Alert>
              )}
              
              {!gatheringsLoading && !gatheringsError && (
                <>
                  {attendedGatherings.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date & Time</TableCell>
                            <TableCell>Title</TableCell>
                            <TableCell>Company</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {attendedGatherings.map((gathering) => (
                            <TableRow key={gathering.id}>
                              <TableCell>
                                <Typography variant="body2">
                                  {formatGatheringDate(gathering.date)} at {formatTime(gathering.start_time)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {gathering.title}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {gathering.company_name || '—'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      No gatherings attended
                    </Typography>
                  )}
                </>
              )}
            </Paper>
          </Box>
        </Grid>
                {/* Notes Section */}
        <Grid size={{ xs: 12 }}>
          <Box sx={{ mb: 3 }}>
            <Paper elevation={2} sx={{ p: 3 }}>
              <UserNotes userId={user.id} />
            </Paper>
          </Box>
        </Grid>
      </Grid>

      {/* Notes & Insights Section */}
      <Grid size={{ xs: 12 }}>
        <Box sx={{ mb: 3 }}>
          <UserProfileInsights userId={user.id} />
        </Box>
      </Grid>

      {/* Edit User Profile Dialog */}
      <EditUserProfileDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        user={user}
        onSuccess={() => {
          // Refresh user data after successful update
          onUserDataRefresh?.();
          setEditDialogOpen(false);
        }}
      />

      {/* Remove CaaS Membership Confirmation Dialog */}
      <Dialog 
        open={showRemoveCaasDialog} 
        onClose={() => {
          setShowRemoveCaasDialog(false);
          setRemoveCaasError(null);
        }}
      >
        <DialogTitle>Remove CaaS Membership</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove this user from all CaaS teams and remove their CaaS subscription. This cannot be undone. Are you sure?
          </DialogContentText>
          {removeCaasError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {removeCaasError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setShowRemoveCaasDialog(false);
              setRemoveCaasError(null);
            }} 
            disabled={isRemovingCaas}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRemoveCaasMembership}
            color="error"
            variant="contained"
            disabled={isRemovingCaas}
          >
            {isRemovingCaas ? <CircularProgress size={20} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserDetailContent;
